import React, { useCallback, useMemo, useState } from 'react';
import Typography from '../Typography';
import './header.scss';

interface HeaderProps {
  logout: () => void;
  name: string;
  email: string;
  icon: string | React.ReactNode;
  title: string;
}

const Header: React.FC<HeaderProps> = ({ logout, name, email, icon, title }) => {
  const [showInfo, setShowInfo] = useState(false);

  const toggleInfo = useCallback(() => setShowInfo(prev => !prev), []);

  const onUserInfoKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleInfo();
      }
    },
    [toggleInfo]
  );

  const getInitials = (fullName?: string): string => {
    if (!fullName) return '';
    return fullName
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase();
  };

  const initials = useMemo(() => getInitials(name), [name]);

  return (
    <div className="header-container">
      <div className="application-type">
        {typeof icon === 'string' ? (
          <img src={icon} alt={`${title} icon`} />
        ) : (
          icon
        )}
        <Typography variant="h1">{title}</Typography>
      </div>

      <div className="user-info-wrapper">
        <div
          className="user-info"
          onClick={toggleInfo}
          onKeyDown={onUserInfoKeyDown}
          role="button"
          tabIndex={0}
          aria-label="Toggle User Info"
        >
          <div className="name-email-container desktop-only">
            <div className="user-name">
              <Typography variant="text-3">{name}</Typography>
            </div>
            <div className="user-email">
              <Typography variant="small-text-2">{email}</Typography>
            </div>
          </div>
          <div className="user-image">
            <Typography variant="small-text-2">{initials}</Typography>
          </div>
        </div>

        {showInfo && (
          <div className="user-details">
            <div className="desktop-user-details">
              <Typography variant="subHeading-1">{name}</Typography>
              <Typography variant="body-text-3">{email}</Typography>
            </div>
            <button className="logout-button" onClick={logout}>
              <Typography variant="body-text-1">Sign out</Typography>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(Header);
